import type { ProspectTagSortReq, ProspectTagVo } from 'types/prospectTag';
import { assignProspectTags, deleteProspectTag, getProspectTagList, reorderProspectTags } from 'api/prospect';
import {
    gpGetProspectTagList,
    gpAssignProspectTags,
    gpDeleteProspectTag,
    gpReorderProspectTags,
} from 'api/group';

export function sortTagVo(list: ProspectTagVo[]): ProspectTagVo[] {
    return list.slice().sort((a, b) => a.sortOrder - b.sortOrder);
}

export function tagNamesFromVo(list: ProspectTagVo[]): string[] {
    return sortTagVo(list)
        .map((t) => t.tagName)
        .filter(Boolean);
}

export async function fetchProspectTags(prospectId: number, isPracticeView = false): Promise<ProspectTagVo[]> {
    const res = isPracticeView
        ? await gpGetProspectTagList(prospectId)
        : await getProspectTagList(prospectId);
    return sortTagVo(((res?.data?.data || []) as ProspectTagVo[]) || []);
}

export function diffAddedTags(existing: string[], next: string[]): string[] {
    const ex = new Set((existing || []).map((t) => t.trim()).filter(Boolean));
    return (next || [])
        .map((t) => t.trim())
        .filter(Boolean)
        .filter((t) => !ex.has(t));
}

export async function addProspectTagsIncremental(params: {
    prospectId: number;
    existingTagNames: string[];
    nextTagNames: string[];
    isPracticeView?: boolean;
    identityId?: number;
}): Promise<{ added: string[] }> {
    const { prospectId, existingTagNames, nextTagNames, isPracticeView, identityId } = params;
    const added = diffAddedTags(existingTagNames, nextTagNames);
    if (added.length === 0) return { added: [] };
    if (isPracticeView && identityId) {
        await gpAssignProspectTags({ identityId, tagNames: added });
    } else {
        await assignProspectTags({ prospectId, tagNames: added });
    }
    return { added };
}

export async function removeProspectTagByName(params: {
    prospectId: number;
    tagList: ProspectTagVo[];
    tagName: string;
    isPracticeView?: boolean;
}): Promise<{ removed: boolean }> {
    const { tagList, tagName, isPracticeView } = params;
    const vo = tagList.find((x) => x.tagName === tagName);
    if (!vo?.id) return { removed: false };
    if (isPracticeView) {
        await gpDeleteProspectTag(vo.id);
    } else {
        await deleteProspectTag(vo.id);
    }
    return { removed: true };
}

export async function reorderProspectTagsByNames(params: {
    prospectId: number;
    tagList: ProspectTagVo[];
    nextTagNames: string[];
    isPracticeView?: boolean;
    identityId?: number;
}): Promise<{ items: ProspectTagSortReq[] }> {
    const { prospectId, tagList, nextTagNames, isPracticeView, identityId } = params;
    const byName = new Map(tagList.map((t) => [t.tagName, t] as const));
    const items = (nextTagNames || [])
        .map((name, idx) => {
            const t = byName.get(name);
            if (!t?.id) return null;
            return { id: t.id, sortOrder: idx };
        })
        .filter(Boolean) as ProspectTagSortReq[];

    if (items.length === 0) return { items: [] };
    if (isPracticeView && identityId) {
        await gpReorderProspectTags(identityId, items.map((item, idx) => ({
            sortOrder: idx,
            tagName: nextTagNames[idx],
        })));
    } else {
        await reorderProspectTags(prospectId, items);
    }
    return { items };
}
